import { Header } from '@/components/layout/Header';
import { Footer } from '@/components/layout/Footer';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircle, Wrench } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';

const FinancialTools = () => {
  return (
    <div className="min-h-screen bg-[#F7F6F2]">
      <Helmet>
        <title>Financial Tools & Resources | KGOB CPAs</title>
        <meta name="description" content="Simple, self-serve wins without waiting. Access curated tools and resources to get started on your own time." />
        <link rel="canonical" href="https://kgob.com/services/tools" />
      </Helmet>
      
      <Header />
      
      <main>
        {/* Hero Section */}
        <section className="relative bg-gradient-to-b from-[#0B2240] via-[#0f2d4f] to-[#0B2240] py-20 lg:py-32">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="max-w-4xl mx-auto text-center"
            >
              <div className="flex justify-center mb-6">
                <Wrench className="w-16 h-16 text-[#D4AF37]" />
              </div>
              
              <h1 className="font-['Playfair_Display'] text-5xl font-bold text-[#F7F6F2] mb-6">
                Financial Tools & Resources
              </h1>
              
              <p className="text-xl text-[#D4AF37] italic mb-4">
                Simple, self-serve wins without waiting.
              </p>
              
              <p className="text-lg text-[#F7F6F2]/90 mb-6">
                For those who like to get started on their own time, we offer curated tools and resources through our sister platforms. These complement our full services or give you a starting point.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center mt-10">
                <Button data-testid="financial-tools-button-4" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                  <Link data-testid="financial-tools-link-4" to="/book-consultation?source=services-tools">Explore Tools & Resources</Link>
                </Button>
                <Button data-testid="financial-tools-button-3" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                  <Link data-testid="financial-tools-link-3" to="/services">View All Services</Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Is This Right for You? */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-8 text-center">
              Is This Right for You?
            </h2>
            
            <Card className="p-8 border-2 border-[#E1DFD7]">
              <p className="text-[#4C5A70] mb-6">
                Our tools and resources are designed for business owners and individuals who want to learn, plan, and take action on their own time. You will benefit most if:
              </p>
              
              <div className="space-y-3">
                {[
                  'You prefer to research and learn before engaging with a CPA',
                  'You want self-serve tools for tax planning, projections, or business decisions',
                  'You are looking for guides, templates, or calculators',
                  'You want access to KGOB\'s sister platforms (AskSara, BizHub, Let\'s Talk Growth)',
                  'You want to complement full-service work with DIY resources'
                ].map((item, i) => (
                  <div key={i} className="flex gap-3">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4C5A70]">{item}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </section>

        {/* What You Get */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What You Get
            </h2>
            
            <div className="space-y-8">
              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Tax Planning Tools</h3>
                <p className="text-[#4C5A70] mb-4">
                  Self-serve tax planning spreadsheets, guides, and calculators to help you estimate taxes, plan deductions, and model scenarios.
                </p>
                <ul className="space-y-2">
                  {[
                    'Federal tax planner spreadsheets',
                    'S corporation savings calculator',
                    'Estimated tax payment calculator',
                    'Tax strategy guides for business owners'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Sister Platform Access</h3>
                <p className="text-[#4C5A70] mb-4">
                  KGOB clients get access to our sister platforms, including AskSara (AI tax assistant), BizHub (business resources), and Let\'s Talk Growth (valuation tools).
                </p>
                <ul className="space-y-2">
                  {[
                    'AskSara: AI-powered tax question assistant',
                    'BizHub: Business templates, guides, and calculators',
                    'Let\'s Talk Growth: Valuation and growth planning tools',
                    'Let\'s Talk Taxes: Tax education and planning resources'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>

              <Card className="p-8 border-2 border-[#E1DFD7]">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#0B2240] mb-4">Guides & Templates</h3>
                <p className="text-[#4C5A70] mb-4">
                  Downloadable guides, checklists, and templates for common tax and business questions.
                </p>
                <ul className="space-y-2">
                  {[
                    'New business startup checklist',
                    'S corporation election guide',
                    'Year-end tax planning checklist',
                    'Bookkeeping best practices guide'
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3">
                      <CheckCircle className="w-4 h-4 text-[#D4AF37] mt-1 flex-shrink-0" />
                      <span className="text-sm text-[#4C5A70]">{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              How It Works
            </h2>
            
            <div className="space-y-6">
              {[
                { step: '1', title: 'Request Access', desc: 'Schedule a consultation or contact us to request access to our tools and sister platforms.' },
                { step: '2', title: 'Get Your Credentials', desc: 'We provide login credentials and links to all relevant platforms and resources.' },
                { step: '3', title: 'Explore & Use', desc: 'Browse tools, guides, and calculators at your own pace. Use them for planning, learning, or decision-making.' },
                { step: '4', title: 'Ask Questions', desc: 'If you get stuck or need guidance, reach out to your KGOB team for support.' },
                { step: '5', title: 'Upgrade to Full Service', desc: 'If you decide you want full CPA support, we can transition you to bookkeeping, tax, or advisory services.' }
              ].map((item, i) => (
                <Card key={i} className="p-6 border-2 border-[#E1DFD7]">
                  <div className="flex gap-4">
                    <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                      <span className="text-[#0B2240] font-bold text-lg">{item.step}</span>
                    </div>
                    <div>
                      <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-2">{item.title}</h3>
                      <p className="text-[#4C5A70]">{item.desc}</p>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* What Changes */}
        <section className="py-20 bg-[#F7F6F2]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-5xl">
            <h2 className="font-['Playfair_Display'] text-4xl font-semibold text-[#0B2240] mb-12 text-center">
              What Changes
            </h2>
            
            <div className="grid md:grid-cols-3 gap-6">
              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Functional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You have access to tools, guides, and calculators to help you plan, learn, and make better decisions.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Emotional</h3>
                <p className="text-[#4C5A70] text-sm">
                  You feel empowered to tackle tax and business questions on your own time, at your own pace.
                </p>
              </Card>

              <Card className="p-6 border-2 border-[#E1DFD7] text-center">
                <div className="mb-4">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#0B2240]" />
                  </div>
                </div>
                <h3 className="font-['Playfair_Display'] text-xl text-[#0B2240] mb-3">Strategic</h3>
                <p className="text-[#4C5A70] text-sm">
                  With better information and planning tools, you can make smarter decisions about your financial future.
                </p>
              </Card>
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-b from-[#0B2240] to-[#0f2d4f]">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-4xl text-center">
            <h2 className="font-['Playfair_Display'] text-4xl font-bold text-[#F7F6F2] mb-6">
              Ready to Explore Our Tools?
            </h2>
            <p className="text-lg text-[#F7F6F2]/90 mb-10">
              Schedule a consultation to learn more about our tools and resources. We will get you set up with access to everything you need.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button data-testid="financial-tools-button-2" asChild className="h-14 px-8 bg-[linear-gradient(180deg,#E8D89E_0%,#D4AF37_50%,#B99610_100%)] text-[#0B2240]">
                <Link data-testid="financial-tools-link-2" to="/book-consultation?source=services-tools">Explore Tools & Resources</Link>
              </Button>
              <Button data-testid="financial-tools-button-1" asChild variant="outline" className="h-14 px-8 border-2 border-[#F7F6F2] text-[#F7F6F2]">
                <Link data-testid="financial-tools-link-1" to="/services">View All Services</Link>
              </Button>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </div>
  );
};

export default FinancialTools;